/**
 * AI Credentials onboarding gate.
 *
 * Shown once to authenticated users who have not yet configured a Gemini or
 * OpenRouter key for Cortex. Uses the same Supabase-backed persistence as the
 * feature showcase so dismiss/complete state survives across devices.
 */

import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useAISettings } from "@/hooks/useAISettings";
import { CortexAISetupWizard, useCortexCredentialStatus } from "@flowstate/cortex-auth";
import { supabase } from "@/integrations/supabase/client";
import { createSupabasePersistence } from "@/services/onboarding/supabase-persistence";
import type { UserAISettings } from "@flowstate/types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const FLOW_ID = "ai-credentials-onboarding";

export function AICredentialsOnboarding() {
  const { user, session } = useAuth();
  const { settings, updateSettings } = useAISettings();
  const { isReady, isLoading } = useCortexCredentialStatus(
    user ? supabase : null,
    settings,
  );
  const persistence = useMemo(() => createSupabasePersistence(), []);
  const [dismissed, setDismissed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    persistence.load(FLOW_ID).then((state) => {
      if (cancelled) return;
      if (state?.dismissedAt || state?.completed) {
        setDismissed(true);
      }
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [persistence]);

  const handleSettingsChanged = (updated: Partial<UserAISettings>) => {
    updateSettings(updated);
  };

  const handleComplete = () => {
    persistence.save(FLOW_ID, {
      currentSlideIndex: 0,
      completed: true,
      completedAt: new Date().toISOString(),
    }).catch(() => {
      // Best-effort persistence
    });
  };

  const handleDismiss = () => {
    setDismissed(true);
    persistence.save(FLOW_ID, {
      currentSlideIndex: 0,
      completed: false,
      dismissedAt: new Date().toISOString(),
    }).catch(() => {
      // Best-effort persistence
    });
  };

  if (!loaded || isLoading || !user || isReady || dismissed) {
    return null;
  }

  return (
    <Dialog open modal>
      <DialogContent
        className="max-w-xl max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Welcome to Cortex
          </DialogTitle>
          <DialogDescription>
            Cortex needs your own AI provider credentials before it can chat.
            Choose Gemini (free tier available) or OpenRouter below.
          </DialogDescription>
        </DialogHeader>

        <CortexAISetupWizard
          supabase={supabase}
          accessToken={session?.access_token ?? ""}
          supabaseUrl={import.meta.env.VITE_SUPABASE_URL ?? ""}
          settings={settings}
          onSettingsChanged={handleSettingsChanged}
          onComplete={handleComplete}
          mode="gate"
        />

        <div className="flex justify-end pt-2">
          <Button variant="ghost" size="sm" onClick={handleDismiss}>
            Remind me later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
